import { useState } from 'react'
import toast from 'react-hot-toast'
import { useAuth } from '../context/AuthContext'
import { formatCurrency, CURRENCIES } from '../utils/constants'
import Icon from '../components/atoms/Icon'

const Anticipos = () => {
  const { user } = useAuth()
  const [formData, setFormData] = useState({
    valor: '',
    descripcion: '',
    referencia: ''
  })
  const [currency, setCurrency] = useState('COP')
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrors({})

    const newErrors = {}

    if (!formData.valor.trim()) {
      newErrors.valor = 'El valor es requerido'
    } else if (isNaN(parseFloat(formData.valor)) || parseFloat(formData.valor) <= 0) {
      newErrors.valor = 'El valor debe ser un número mayor a cero'
    }

    if (!formData.descripcion.trim()) {
      newErrors.descripcion = 'La descripción es requerida'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setLoading(true)
    try {
      const token = localStorage.getItem('auth_token')

      const pagoData = {
        valor: parseFloat(formData.valor),
        descripcion: formData.descripcion.trim(),
        referencia: formData.referencia.trim() || null,
        divisa: currency,
        tipo: 'ANTICIPO'
      }

      const response = await fetch('/api/payments/anticipo', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(pagoData)
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Error al procesar el pago de anticipo')
      }

      if (data.redirectUrl) {
        window.location.href = data.redirectUrl
      } else if (data.success) {
        toast.success('Anticipo registrado exitosamente')
        setFormData({
          valor: '',
          descripcion: '',
          referencia: ''
        })
      }

    } catch (error) {
      console.error('Error procesando anticipo:', error)
      toast.error(error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const valorFormateado = formData.valor ?
    formatCurrency(parseFloat(formData.valor) || 0, currency) : null

  return (
    <div className="flex flex-col space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Pagos de Anticipos
        </h1>
        <p className="text-gray-600">
          Realice pagos de anticipos de forma manual a través del sistema PSE
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Datos del Anticipo
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="valor" className="block text-sm font-medium text-gray-700 mb-2">
                Valor del Anticipo *
              </label>
              <div className="relative">
                <input
                  type="number"
                  id="valor"
                  name="valor"
                  value={formData.valor}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-ec-primary-500 focus:border-transparent
                    ${errors.valor ? 'border-red-300' : 'border-gray-300'}`}
                  placeholder="0.00"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className="bg-transparent border-none text-sm text-gray-600 focus:outline-none"
                  >
                    {Object.entries(CURRENCIES).map(([code, info]) => (
                      <option key={code} value={code}>
                        {info.symbol}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {errors.valor && (
                <p className="mt-1 text-sm text-red-600">{errors.valor}</p>
              )}
              {valorFormateado && (
                <p className="mt-1 text-sm text-gray-500">
                  Valor: {valorFormateado}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="descripcion" className="block text-sm font-medium text-gray-700 mb-2">
                Descripción del Anticipo *
              </label>
              <textarea
                id="descripcion"
                name="descripcion"
                rows={3}
                value={formData.descripcion}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-ec-primary-500 focus:border-transparent
                  ${errors.descripcion ? 'border-red-300' : 'border-gray-300'}`}
                placeholder="Describa el motivo o concepto del anticipo"
              />
              {errors.descripcion && (
                <p className="mt-1 text-sm text-red-600">{errors.descripcion}</p>
              )}
            </div>

            <div>
              <label htmlFor="referencia" className="block text-sm font-medium text-gray-700 mb-2">
                Referencia (Opcional)
              </label>
              <input
                type="text"
                id="referencia"
                name="referencia"
                value={formData.referencia}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ec-primary-500 focus:border-transparent"
                placeholder="Número de orden, proyecto, etc."
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center space-x-2 py-3 px-4 border border-transparent
                       rounded-lg shadow-sm text-sm font-medium text-white bg-ec-primary-600 hover:bg-ec-primary-700
                       focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ec-primary-500
                       disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Icon name={loading ? "loader" : "credit-card"} className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span>{loading ? 'Procesando...' : 'Pagar Anticipo'}</span>
            </button>
          </form>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Información de Pago
          </h2>

          <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">Datos de la Empresa</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <div>
                  <span className="font-medium">Razón Social:</span> {user?.razonSocial}
                </div>
                <div>
                  <span className="font-medium">NIT:</span> {user?.nit}
                </div>
                <div>
                  <span className="font-medium">Correo:</span> {user?.email}
                </div>
                {user?.telefono && (
                  <div>
                    <span className="font-medium">Teléfono:</span> {user.telefono}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex">
                <Icon name="info" className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Información sobre Anticipos
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Los anticipos son pagos adelantados que se aplicarán a futuras facturas</li>
                      <li>El pago se procesará a través de PSE (Pagos Seguros en Línea)</li>
                      <li>Recibirá una confirmación por correo electrónico</li>
                      <li>El procesamiento puede tardar entre 5 a 10 minutos</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex">
                <Icon name="alert-triangle" className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Importante
                  </h3>
                  <p className="mt-1 text-sm text-yellow-700">
                    Verifique cuidadosamente los datos antes de proceder con el pago.
                    Los anticipos no son reembolsables una vez procesados.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Anticipos